import { containerStatusLabel, normalizeContainerStatus } from './container-status-presentation.mjs?v=20260806.100';

import {
  kpiSparklineMarkup,
  mountKpiSparklines,
} from './kpi-sparkline.mjs?v=20260806.100';
import { getLocale } from './i18n.mjs?v=20260806.100';

// The nine Ocean Statuses, ordered along the Container's journey.
const OCEAN_FLOW_STAGES = Object.freeze([
  'not_arrived_at_port',
  'available_for_pickup',
  'inspection',
  'inspection_cleared',
  'delivered_to_warehouse',
  'empty_return_reported',
  'empty_returned',
  'pending',
  'cancelled',
]);

// One icon per status, reusing the Phosphor set already loaded for the shell.
const OCEAN_FLOW_ICONS = Object.freeze({
  not_arrived_at_port: 'ph-boat',
  available_for_pickup: 'ph-package',
  inspection: 'ph-magnifying-glass',
  inspection_cleared: 'ph-seal-check',
  delivered_to_warehouse: 'ph-warehouse',
  empty_return_reported: 'ph-clipboard-text',
  empty_returned: 'ph-arrow-counter-clockwise',
  pending: 'ph-clock',
  cancelled: 'ph-prohibit',
});

const INTERNAL_ROLES = new Set(['admin', 'dispatcher', 'clerk', 'finance']);
const COMPLETE_STATUSES = new Set(['empty_returned', 'empty_returned_to_terminal', 'completed']);
const CANCELLED_STATUSES = new Set(['cancelled', 'canceled']);
const MONTH_KEYS = Object.freeze(Array.from({ length: 12 }, (_, index) => index));

const COPY = Object.freeze({
  en: Object.freeze({
    revenue: 'Total Revenue (YTD)',
    handled: 'Containers Handled (YTD)',
    completed: 'Deliveries Completed (YTD)',
    profit: 'Gross Profit (YTD)',
    active: 'Active Containers (Live)',
    lastYear: 'vs Last Year',
    yesterday: 'vs Yesterday',
    revenueTrend: 'Revenue Trend (YTD)',
    flow: 'Container Flow (YTD)',
    status: 'Container Status (Live)',
    total: 'Total',
    booked: 'Booked',
    pickedUp: 'Picked Up',
    inTransit: 'In Transit',
    atPort: 'At Port',
    delivered: 'Delivered',
    returned: 'Returned',
    loaded: 'Loaded',
    empty: 'Empty',
    waitingReturn: 'Waiting Return',
    dispatches: 'Dispatches Assigned',
    dispatchTime: 'Avg Dispatch Time',
    deliveries: 'Deliveries Completed',
    onTime: 'On-Time Rate',
    documents: 'Documents Processed',
    completionRate: 'Completion Rate',
    invoices: 'Invoices Processed',
    payments: 'Payments Received',
    unavailable: '—',
  }),
  zh: Object.freeze({
    revenue: '总营收（本年度）',
    handled: '处理柜量（本年度）',
    completed: '完成运输（本年度）',
    profit: '毛利润（本年度）',
    active: '活跃集装箱（实时）',
    lastYear: '较去年同期',
    yesterday: '较昨日',
    revenueTrend: '营收趋势（本年度）',
    flow: '集装箱流程（本年度）',
    status: '集装箱状态（实时）',
    total: '合计',
    booked: '已预订',
    pickedUp: '已提柜',
    inTransit: '运输中',
    atPort: '在港',
    delivered: '已送达',
    returned: '已还空',
    loaded: '重柜',
    empty: '空柜',
    waitingReturn: '等待还空',
    dispatches: '今日派车',
    dispatchTime: '平均派车时间',
    deliveries: '今日完成运输',
    onTime: '准时率',
    documents: '今日处理文件',
    completionRate: '完成率',
    invoices: '今日处理账单',
    payments: '已收款',
    unavailable: '—',
  }),
});

function copy(locale = getLocale()) {
  return String(locale).toLowerCase().startsWith('zh') ? COPY.zh : COPY.en;
}

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function timestampMillis(value) {
  if (!value) return null;
  if (Number.isFinite(value)) return Number(value);
  if (value instanceof Date) return Number.isFinite(value.valueOf()) ? value.valueOf() : null;
  if (typeof value.toMillis === 'function') {
    const millis = value.toMillis();
    return Number.isFinite(millis) ? millis : null;
  }
  if (typeof value.toDate === 'function') return timestampMillis(value.toDate());
  if (Number.isFinite(value.seconds)) return Number(value.seconds) * 1000;
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? parsed : null;
}

function rowValue(row, key) {
  if (row?.[key] != null) return row[key];
  if (row?.field_values?.[key] != null) return row.field_values[key];
  if (row?.finance_summary?.[key] != null) return row.finance_summary[key];
  return null;
}

function dateFor(row, ...keys) {
  for (const key of keys) {
    const millis = timestampMillis(rowValue(row, key));
    if (Number.isFinite(millis)) return new Date(millis);
  }
  return null;
}

function numeric(row, ...keys) {
  for (const key of keys) {
    const value = Number(rowValue(row, key));
    if (Number.isFinite(value)) return value;
  }
  return 0;
}

function rowStatus(row) {
  return normalizeContainerStatus(
    rowValue(row, 'ocean_status') || rowValue(row, 'status') || rowValue(row, 'current_stage') || '',
  );
}

function isComplete(row) {
  return row?.canonical_completed === true
    || row?.is_empty_returned === true
    || COMPLETE_STATUSES.has(rowStatus(row))
    || Boolean(dateFor(row, 'empty_returned_at_millis', 'empty_returned_at'));
}

function isCancelled(row) {
  return CANCELLED_STATUSES.has(rowStatus(row));
}

function ytdBounds(now) {
  const year = now.getFullYear();
  return {
    start: new Date(year, 0, 1),
    end: new Date(now),
    previousStart: new Date(year - 1, 0, 1),
    previousEnd: new Date(year - 1, now.getMonth(), now.getDate(), 23, 59, 59, 999),
  };
}

function inBounds(date, start, end) {
  return date instanceof Date && date >= start && date <= end;
}

function createdDate(row) {
  return dateFor(row, 'created_at_millis', 'created_at', 'updated_at_millis', 'updated_at');
}

function completedDate(row) {
  return dateFor(
    row,
    'empty_returned_at_millis',
    'empty_returned_at',
    'actual_empty_returned_at_millis',
    'actual_empty_returned_at',
    'updated_at_millis',
    'updated_at',
  );
}

function updatedDate(row) {
  return dateFor(row, 'updated_at_millis', 'updated_at', 'created_at_millis', 'created_at');
}

function dateKey(date, timeZone = 'America/Los_Angeles') {
  const millis = timestampMillis(date);
  if (!Number.isFinite(millis)) return '';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(millis));
  const lookup = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${lookup.year}-${lookup.month}-${lookup.day}`;
}

function moneyCents(row) {
  return numeric(row, 'total_revenue_cents', 'customer_receivable_cents', 'customer_quote_cents');
}

function costCents(row) {
  return numeric(row, 'total_operational_cost_cents', 'total_cost_cents');
}

function profitCents(row) {
  const explicit = Number(rowValue(row, 'profit_cents'));
  return Number.isFinite(explicit) ? explicit : moneyCents(row) - costCents(row);
}

function comparison(current, previous) {
  if (!Number.isFinite(current) || !Number.isFinite(previous) || previous === 0) {
    return Object.freeze({ direction: 'unavailable', value: null });
  }
  const percent = ((current - previous) / Math.abs(previous)) * 100;
  return Object.freeze({
    direction: percent > 0 ? 'up' : percent < 0 ? 'down' : 'flat',
    value: Math.abs(percent),
  });
}

function monthLabels(now, locale) {
  return MONTH_KEYS.map((month) => new Intl.DateTimeFormat(locale, { month: 'short' })
    .format(new Date(now.getFullYear(), month, 15)));
}

function monthlySeries(rows, now, value) {
  const monthly = Array(12).fill(0);
  rows.forEach((row) => {
    const date = createdDate(row);
    if (!date || date.getFullYear() !== now.getFullYear()) return;
    monthly[date.getMonth()] += Number(value(row)) || 0;
  });
  return monthly;
}

function cumulative(values) {
  let running = 0;
  return values.map((value) => {
    running += Number(value) || 0;
    return running;
  });
}

function liveStatusKey(row) {
  const status = rowStatus(row);
  if (dateFor(row, 'empty_picked_up_at_millis', 'empty_picked_up_at')
    || dateFor(row, 'empty_ready_at_millis', 'empty_ready_at')) return 'empty';
  if (status === 'delivered_to_warehouse' || rowValue(row, 'reported_empty') === true) return 'waiting-return';
  if (dateFor(row, 'loaded_delivered_at_millis', 'loaded_delivered_at')) return 'delivered';
  if (dateFor(row, 'load_out_at_millis', 'load_out_at')) return 'loaded';
  if (['in_transit', 'picked_up_from_terminal'].includes(status)) return 'in-transit';
  return 'at-port';
}

function hasDocumentWork(row) {
  return Boolean(
    rowValue(row, 'pre_alert_status')
    || rowValue(row, 'forecast_status')
    || rowValue(row, 'document_ready')
    || rowValue(row, 'do_received'),
  );
}

function hasFinanceWork(row) {
  return Boolean(
    rowValue(row, 'ctf_status')
    || rowValue(row, 'tmf_status')
  );
}

function averageMinutes(values) {
  const finite = values.filter(Number.isFinite);
  if (!finite.length) return null;
  return Math.round(finite.reduce((sum, value) => sum + value, 0) / finite.length);
}

function todayOperations(rows, now, canViewFinance, timeZone) {
  const today = dateKey(now, timeZone);
  const updatedToday = rows.filter((row) => dateKey(updatedDate(row), timeZone) === today);
  const dispatchRows = rows.filter((row) => {
    const assignmentDate = dateFor(
      row,
      'dispatch_task.scheduled_start_at_millis',
      'pickup_appointment_at_millis',
      'pickup_appointment_at',
    );
    return dateKey(assignmentDate, timeZone) === today && Boolean(
      rowValue(row, 'pickup_driver_name')
      || rowValue(row, 'assigned_driver_name')
      || row?.dispatch_task?.driver_name,
    );
  });
  const dispatchMinutes = dispatchRows.map((row) => {
    const created = timestampMillis(createdDate(row));
    const assigned = timestampMillis(dateFor(row, 'assigned_at_millis', 'assigned_at'));
    return Number.isFinite(created) && Number.isFinite(assigned) && assigned >= created
      ? (assigned - created) / 60000 : null;
  });
  const completedToday = rows.filter((row) => isComplete(row)
    && dateKey(completedDate(row), timeZone) === today);
  const onTimeRows = completedToday.map((row) => {
    const completed = timestampMillis(completedDate(row));
    const deadline = timestampMillis(dateFor(row, 'return_empty_lfd_at_millis', 'return_empty_lfd_at'));
    return Number.isFinite(completed) && Number.isFinite(deadline) ? completed <= deadline : null;
  }).filter((value) => value !== null);
  const documentRows = updatedToday.filter(hasDocumentWork);
  const financeRows = updatedToday.filter(hasFinanceWork);
  const receivedCents = updatedToday
    .filter((row) => String(rowValue(row, 'remittance_status') || '').toLowerCase() === 'received')
    .reduce((sum, row) => sum + moneyCents(row), 0);
  return Object.freeze({
    dispatcher: Object.freeze({
      primaryValue: dispatchRows.length,
      secondaryValue: averageMinutes(dispatchMinutes),
    }),
    driver: Object.freeze({
      primaryValue: completedToday.length,
      secondaryValue: onTimeRows.length
        ? Math.round((onTimeRows.filter(Boolean).length / onTimeRows.length) * 100) : null,
    }),
    clerk: Object.freeze({
      primaryValue: documentRows.length,
      secondaryValue: updatedToday.length
        ? Math.round((documentRows.length / updatedToday.length) * 100) : null,
    }),
    finance: Object.freeze({
      primaryValue: canViewFinance ? financeRows.length : null,
      secondaryValue: canViewFinance ? receivedCents : null,
    }),
  });
}

export function statusPageAvailable(role) {
  return INTERNAL_ROLES.has(String(role || '').toLowerCase());
}

export function statusPageModel({
  rows = [],
  role = '',
  now = new Date(),
  timeZone = 'America/Los_Angeles',
} = {}) {
  const safeRole = String(role || '').toLowerCase();
  const safeNow = timestampMillis(now) ? new Date(timestampMillis(now)) : new Date();
  const bounds = ytdBounds(safeNow);
  const source = Array.isArray(rows) ? rows : [];
  const ytdRows = source.filter((row) => inBounds(createdDate(row), bounds.start, bounds.end));
  const previousRows = source.filter((row) => inBounds(createdDate(row), bounds.previousStart, bounds.previousEnd));
  const liveRows = source.filter((row) => !isComplete(row) && !isCancelled(row));
  const canViewFinance = ['admin', 'finance'].includes(safeRole);
  const revenue = ytdRows.reduce((sum, row) => sum + moneyCents(row), 0);
  const previousRevenue = previousRows.reduce((sum, row) => sum + moneyCents(row), 0);
  const completed = ytdRows.filter(isComplete).length;
  const previousCompleted = previousRows.filter(isComplete).length;
  const profit = ytdRows.reduce((sum, row) => sum + profitCents(row), 0);
  const previousProfit = previousRows.reduce((sum, row) => sum + profitCents(row), 0);
  const revenueMonthly = cumulative(monthlySeries(ytdRows, safeNow, moneyCents));
  const handledMonthly = cumulative(monthlySeries(ytdRows, safeNow, () => 1));
  const completedMonthly = cumulative(monthlySeries(ytdRows.filter(isComplete), safeNow, () => 1));
  const profitMonthly = cumulative(monthlySeries(ytdRows, safeNow, profitCents));
  const activeMonthly = cumulative(monthlySeries(ytdRows.filter((row) => !isComplete(row) && !isCancelled(row)), safeNow, () => 1));
  const labels = monthLabels(safeNow, String(getLocale()).startsWith('zh') ? 'zh-CN' : 'en-US');
  // The flow runs on the nine Ocean Statuses a Container can actually hold, in lifecycle order
  // (Alan, 2026-07-31). It used to run on six invented stages — booked, picked-up, in-transit and
  // so on — detected by heuristics like "does this row have a driver name". Those stages
  // overlapped, so the numbers never added up to anything, and none of the words appeared
  // anywhere else in the product. A Container is in exactly one status, so these counts partition
  // the year: they sum to the total, and they use the same vocabulary as the table.
  const flowKeys = OCEAN_FLOW_STAGES;
  const liveStatus = [
    ['loaded', '#146BFF'],
    ['empty', '#11B981'],
    ['in-transit', '#FF8A00'],
    ['at-port', '#7A3FF2'],
    ['delivered', '#F5B51B'],
    ['waiting-return', '#F04438'],
  ].map(([key, color]) => Object.freeze({
    key,
    color,
    value: liveRows.filter((row) => liveStatusKey(row) === key).length,
  }));
  return Object.freeze({
    role: safeRole,
    canViewFinance,
    now: safeNow,
    timeZone,
    labels: Object.freeze(labels),
    executive: Object.freeze({
      revenueCents: canViewFinance ? revenue : null,
      containersHandled: ytdRows.length,
      deliveriesCompleted: completed,
      grossProfitCents: canViewFinance ? profit : null,
      activeContainers: liveRows.length,
      revenueTrend: canViewFinance ? comparison(revenue, previousRevenue) : comparison(NaN, NaN),
      handledTrend: comparison(ytdRows.length, previousRows.length),
      completedTrend: comparison(completed, previousCompleted),
      profitTrend: canViewFinance ? comparison(profit, previousProfit) : comparison(NaN, NaN),
      activeTrend: comparison(NaN, NaN),
      revenueSeries: Object.freeze(canViewFinance ? revenueMonthly : Array(12).fill(null)),
      handledSeries: Object.freeze(handledMonthly),
      completedSeries: Object.freeze(completedMonthly),
      profitSeries: Object.freeze(canViewFinance ? profitMonthly : Array(12).fill(null)),
      activeSeries: Object.freeze(activeMonthly),
    }),
    revenueTrend: Object.freeze({
      values: Object.freeze(canViewFinance ? revenueMonthly : Array(12).fill(null)),
      labels: Object.freeze(labels),
    }),
    flow: Object.freeze(flowKeys.map((key) => Object.freeze({
      key,
      value: ytdRows.filter((row) => normalizeContainerStatus(rowStatus(row)) === key).length,
    }))),
    containerStatus: Object.freeze(liveStatus),
    capacityPercent: null,
    operations: todayOperations(source, safeNow, canViewFinance, timeZone),
  });
}

function formatCompactMoney(cents, locale) {
  if (cents == null) return '—';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 2,
  }).format((Number(cents) || 0) / 100);
}

function formatExactMoney(cents, locale) {
  if (cents == null) return '—';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format((Number(cents) || 0) / 100);
}

function trendMarkup(trend, context, locale) {
  const direction = trend?.direction || 'unavailable';
  const arrow = direction === 'up' ? '↑' : direction === 'down' ? '↓' : direction === 'flat' ? '→' : '';
  const value = Number.isFinite(trend?.value)
    ? `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(trend.value)}%`
    : '—';
  return `<span class="business-executive-kpi__trend business-executive-kpi__trend--${escapeHtml(direction)}"><b>${escapeHtml(`${arrow} ${value}`.trim())}</b><small>${escapeHtml(context)}</small></span>`;
}

function executiveKpi({
  key, icon, title, value, trend, context, series, labels, displayValues, locale,
}) {
  return `<article class="business-executive-kpi business-executive-kpi--${escapeHtml(key)}">
    <header><span class="business-executive-kpi__icon" aria-hidden="true"><i class="ph ${escapeHtml(icon)}"></i></span><h2>${escapeHtml(title)}</h2></header>
    <strong>${escapeHtml(value)}</strong>
    ${trendMarkup(trend, context, locale)}
    <div class="business-executive-kpi__spark">${kpiSparklineMarkup({
      values: series.map((item) => Number(item) || 0),
      labels,
      displayValues,
      ariaLabel: `${title} trend`,
    })}</div>
  </article>`;
}

function revenueChart(model, locale, text) {
  const raw = model.revenueTrend.values.map((value) => Number(value) || 0);
  const max = Math.max(1, ...raw);
  const width = 640;
  const height = 270;
  const left = 54;
  const right = 18;
  const top = 20;
  const bottom = 40;
  const usableWidth = width - left - right;
  const usableHeight = height - top - bottom;
  const points = raw.map((value, index) => ({
    x: left + (usableWidth * index) / Math.max(1, raw.length - 1),
    y: top + usableHeight - (value / max) * usableHeight,
    value: model.canViewFinance ? model.revenueTrend.values[index] : null,
    label: model.revenueTrend.labels[index],
  }));
  const line = points.map((point, index) => `${index ? 'L' : 'M'} ${point.x.toFixed(1)} ${point.y.toFixed(1)}`).join(' ');
  const area = `${line} L ${points.at(-1).x.toFixed(1)} ${(height - bottom).toFixed(1)} L ${points[0].x.toFixed(1)} ${(height - bottom).toFixed(1)} Z`;
  const gridValues = [1, .75, .5, .25, 0];
  const selected = points.at(-1);
  return `<section class="business-analytics-card business-revenue-card" aria-labelledby="businessRevenueTitle">
    <header><span aria-hidden="true"><i class="ph ph-trend-up"></i></span><h2 id="businessRevenueTitle">${escapeHtml(text.revenueTrend)}</h2></header>
    <div class="business-revenue-chart" data-business-revenue-chart>
      <svg viewBox="0 0 ${width} ${height}" role="img" aria-label="${escapeHtml(text.revenueTrend)}">
        <g class="business-revenue-chart__grid" aria-hidden="true">${gridValues.map((ratio) => {
          const y = top + usableHeight * (1 - ratio);
          return `<line x1="${left}" y1="${y.toFixed(1)}" x2="${width - right}" y2="${y.toFixed(1)}"/><text x="${left - 9}" y="${(y + 4).toFixed(1)}" text-anchor="end">${escapeHtml(formatCompactMoney(max * ratio, locale))}</text>`;
        }).join('')}</g>
        <path class="business-revenue-chart__area" d="${area}"/>
        <path class="business-revenue-chart__line" d="${line}"/>
        ${points.map((point, index) => `<circle class="business-revenue-chart__point${index === points.length - 1 ? ' is-selected' : ''}" data-business-revenue-point="${index}" data-business-revenue-label="${escapeHtml(point.label)}" data-business-revenue-display="${escapeHtml(formatExactMoney(point.value, locale))}" cx="${point.x.toFixed(1)}" cy="${point.y.toFixed(1)}" r="${index === points.length - 1 ? 5 : 3.6}" tabindex="0" role="button" aria-label="${escapeHtml(`${point.label}: ${formatExactMoney(point.value, locale)}`)}"/>`).join('')}
        <g class="business-revenue-chart__months" aria-hidden="true">${points.map((point) => `<text x="${point.x.toFixed(1)}" y="${height - 10}" text-anchor="middle">${escapeHtml(point.label)}</text>`).join('')}</g>
      </svg>
      <div class="business-revenue-tooltip" data-business-revenue-tooltip style="--business-revenue-x:${((selected.x / width) * 100).toFixed(2)}%;--business-revenue-y:${((selected.y / height) * 100).toFixed(2)}%">
        <small>${escapeHtml(selected.label)}</small><strong>${escapeHtml(formatExactMoney(selected.value, locale))}</strong>
      </div>
    </div>
  </section>`;
}

function flowCard(model, locale, text) {
  // Labels come from the shared status vocabulary, so the flow says exactly what the Ocean Status
  // column and the detail page say. A private label table here is how the two drift apart.
  return `<section class="business-analytics-card business-flow-card" aria-labelledby="businessFlowTitle">
    <header><span aria-hidden="true"><i class="ph ph-chart-line-up"></i></span><h2 id="businessFlowTitle">${escapeHtml(text.flow)}</h2></header>
    <div class="business-flow" role="img" aria-label="${escapeHtml(text.flow)}">
      ${model.flow.map((stage, index) => {
    const icon = OCEAN_FLOW_ICONS[stage.key] || 'ph-package';
    const label = containerStatusLabel(stage.key, locale);
    return `<div class="business-flow-stage business-flow-stage--${escapeHtml(stage.key)}" data-flow-status="${escapeHtml(stage.key)}">
          <span>${escapeHtml(label)}</span><i class="ph ${escapeHtml(icon)}" aria-hidden="true"></i><strong>${Number(stage.value || 0).toLocaleString(locale)}</strong>
        </div>${index < model.flow.length - 1 ? '<i class="ph ph-arrow-right business-flow__arrow" aria-hidden="true"></i>' : ''}`;
  }).join('')}
    </div>
  </section>`;
}

function donutStyle(items) {
  const total = items.reduce((sum, item) => sum + item.value, 0);
  if (!total) return '#edf1f6';
  let cursor = 0;
  const parts = items.map((item, index) => {
    const start = cursor;
    const end = index === items.length - 1
      ? 100
      : cursor + (item.value / total) * 100;
    cursor = end;
    return `${item.color} ${start.toFixed(3)}% ${end.toFixed(3)}%`;
  });
  return `conic-gradient(${parts.join(',')})`;
}

function statusCard(model, locale, text) {
  const labels = {
    loaded: text.loaded,
    empty: text.empty,
    'in-transit': text.inTransit,
    'at-port': text.atPort,
    delivered: text.delivered,
    'waiting-return': text.waitingReturn,
  };
  const total = model.containerStatus.reduce((sum, item) => sum + item.value, 0);
  return `<section class="business-analytics-card business-container-status-card" aria-labelledby="businessContainerStatusTitle">
    <header><span aria-hidden="true"><i class="ph ph-chart-donut"></i></span><h2 id="businessContainerStatusTitle">${escapeHtml(text.status)}</h2></header>
    <div class="business-container-status">
      <div>
        <div class="business-status-donut" style="--business-status-donut:${escapeHtml(donutStyle(model.containerStatus))}" role="img" aria-label="${escapeHtml(`${total} ${text.total}`)}"><span><strong>${total.toLocaleString(locale)}</strong><small>${escapeHtml(text.total)}</small></span></div>
      </div>
      <div class="business-status-legend">${model.containerStatus.map((item) => {
        const percent = total ? (item.value / total) * 100 : 0;
        return `<div class="business-status-legend__row"><i style="--status-color:${escapeHtml(item.color)}" aria-hidden="true"></i><span>${escapeHtml(labels[item.key])}</span><strong>${item.value.toLocaleString(locale)}</strong><b>${percent.toFixed(1)}%</b></div>`;
      }).join('')}</div>
    </div>
  </section>`;
}

function operationsCard({ key, icon, title, value, secondaryTitle, secondaryValue, trend = null }) {
  return `<article class="business-operations-kpi business-operations-kpi--${escapeHtml(key)}">
    <header><span aria-hidden="true"><i class="ph ${escapeHtml(icon)}"></i></span><div><h2>${escapeHtml(title)}</h2><strong>${escapeHtml(value)}</strong></div></header>
    <div class="business-operations-kpi__secondary"><span>${escapeHtml(secondaryTitle)}</span><strong>${escapeHtml(secondaryValue)}</strong>${trend == null ? '' : `<b>↑ ${escapeHtml(trend)}</b>`}</div>
  </article>`;
}

export function statusOperationsMarkup({ model, locale = getLocale() } = {}) {
  if (!model || !statusPageAvailable(model.role)) return '';
  const text = copy(locale);
  const exactRevenue = model.executive.revenueSeries.map((value) => formatExactMoney(value, locale));
  const exactProfit = model.executive.profitSeries.map((value) => formatExactMoney(value, locale));
  const activeSeries = model.executive.activeSeries;
  const operation = model.operations;
  const minute = operation.dispatcher.secondaryValue == null
    ? text.unavailable : `${operation.dispatcher.secondaryValue} min`;
  const onTime = operation.driver.secondaryValue == null
    ? text.unavailable : `${operation.driver.secondaryValue}%`;
  const documentRate = operation.clerk.secondaryValue == null
    ? text.unavailable : `${operation.clerk.secondaryValue}%`;
  return `<main class="business-status-page" data-business-status-page>
    <section class="business-status-hero" aria-label="AY TMS business performance">
      <div class="business-executive-grid">
        ${executiveKpi({
          key: 'revenue', icon: 'ph-currency-dollar', title: text.revenue,
          value: formatCompactMoney(model.executive.revenueCents, locale),
          trend: model.executive.revenueTrend, context: text.lastYear,
          series: model.executive.revenueSeries, labels: model.labels, displayValues: exactRevenue, locale,
        })}
        ${executiveKpi({
          key: 'handled', icon: 'ph-package', title: text.handled,
          value: model.executive.containersHandled.toLocaleString(locale),
          trend: model.executive.handledTrend, context: text.lastYear,
          series: model.executive.handledSeries, labels: model.labels, locale,
        })}
        ${executiveKpi({
          key: 'completed', icon: 'ph-truck', title: text.completed,
          value: model.executive.deliveriesCompleted.toLocaleString(locale),
          trend: model.executive.completedTrend, context: text.lastYear,
          series: model.executive.completedSeries, labels: model.labels, locale,
        })}
        ${executiveKpi({
          key: 'profit', icon: 'ph-chart-line-up', title: text.profit,
          value: formatCompactMoney(model.executive.grossProfitCents, locale),
          trend: model.executive.profitTrend, context: text.lastYear,
          series: model.executive.profitSeries, labels: model.labels, displayValues: exactProfit, locale,
        })}
        ${executiveKpi({
          key: 'active', icon: 'ph-package', title: text.active,
          value: model.executive.activeContainers.toLocaleString(locale),
          trend: model.executive.activeTrend, context: text.yesterday,
          series: activeSeries, labels: model.labels, locale,
        })}
      </div>
    </section>
    <section class="business-analytics-grid">
      ${revenueChart(model, locale, text)}
      ${flowCard(model, locale, text)}
      ${statusCard(model, locale, text)}
    </section>
    <section class="business-operations-grid" aria-label="Today operations">
      ${operationsCard({ key: 'dispatcher', icon: 'ph-user', title: text.dispatches, value: operation.dispatcher.primaryValue.toLocaleString(locale), secondaryTitle: text.dispatchTime, secondaryValue: minute })}
      ${operationsCard({ key: 'driver', icon: 'ph-steering-wheel', title: text.deliveries, value: operation.driver.primaryValue.toLocaleString(locale), secondaryTitle: text.onTime, secondaryValue: onTime })}
      ${operationsCard({ key: 'clerk', icon: 'ph-file-text', title: text.documents, value: operation.clerk.primaryValue.toLocaleString(locale), secondaryTitle: text.completionRate, secondaryValue: documentRate })}
      ${operationsCard({ key: 'finance', icon: 'ph-currency-dollar', title: text.invoices, value: operation.finance.primaryValue == null ? text.unavailable : operation.finance.primaryValue.toLocaleString(locale), secondaryTitle: text.payments, secondaryValue: formatCompactMoney(operation.finance.secondaryValue, locale) })}
    </section>
  </main>`;
}

export function mountStatusTimeline(root) {
  const page = root?.querySelector?.('[data-business-status-page]');
  const kpiController = mountKpiSparklines(page);
  const chart = page?.querySelector?.('[data-business-revenue-chart]');
  const tooltip = chart?.querySelector?.('[data-business-revenue-tooltip]');
  const points = chart ? [...chart.querySelectorAll('[data-business-revenue-point]')] : [];
  const cleanups = [];
  const selectPoint = (point) => {
    if (!point || !tooltip) return;
    const svg = point.ownerSVGElement;
    const width = Number(svg?.viewBox?.baseVal?.width) || 640;
    const height = Number(svg?.viewBox?.baseVal?.height) || 270;
    const x = Number(point.getAttribute('cx')) || 0;
    const y = Number(point.getAttribute('cy')) || 0;
    points.forEach((item) => item.classList.toggle('is-selected', item === point));
    tooltip.style.setProperty('--business-revenue-x', `${(x / width) * 100}%`);
    tooltip.style.setProperty('--business-revenue-y', `${(y / height) * 100}%`);
    tooltip.querySelector('small')?.replaceChildren(point.dataset.businessRevenueLabel || '');
    tooltip.querySelector('strong')?.replaceChildren(point.dataset.businessRevenueDisplay || '—');
  };
  if (chart && points.length) {
    const onPointerMove = (event) => {
      const rect = chart.getBoundingClientRect();
      if (!rect.width) return;
      const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
      selectPoint(points[Math.round(ratio * (points.length - 1))]);
    };
    const onFocus = (event) => {
      const point = event.target.closest?.('[data-business-revenue-point]');
      if (point) selectPoint(point);
    };
    chart.addEventListener('pointermove', onPointerMove);
    chart.addEventListener('focusin', onFocus);
    cleanups.push(() => chart.removeEventListener('pointermove', onPointerMove));
    cleanups.push(() => chart.removeEventListener('focusin', onFocus));
  }
  return Object.freeze({
    destroy() {
      cleanups.splice(0).forEach((cleanup) => cleanup());
      kpiController.destroy();
    },
  });
}
